"use client"

import * as React from "react"
import { NavLink, useLocation } from "react-router-dom"
import {
  LayoutDashboard,
  Users,
  Briefcase,
  FileText,
  Plus,
  List,
  Mail,
  Send,
  ChevronDown,
  UserCircle,
  UserCog,
  Settings,
  UserPlus,
} from "lucide-react"
import { useAuth } from "@/contexts/AuthContext"

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar"

const navItems = [
  { title: "לוח בקרה", url: "/dashboard", icon: LayoutDashboard, tour: "tour-nav-dashboard" },
  {
    title: "מועמדים",
    icon: UserCircle,
    tour: "tour-nav-candidates",
    items: [
      { title: "רשימת מועמדים", url: "/candidates", icon: List },
      { title: "העלאת קורות חיים", url: "/upload", icon: Users },
    ],
  },
  {
    title: "שאלונים",
    icon: FileText,
    tour: "tour-nav-forms",
    items: [
      { title: "רשימת שאלונים", url: "/forms", icon: Briefcase },
      { title: "יצירת שאלון", url: "/recruiter", icon: Plus },
      { title: "שליחת מבחן מוכן", url: "/send-ready-test", icon: Mail },
      { title: "שאלונים שנשלחו", url: "/sentforms", icon: Send },
    ],
  },
  {
    title: "מנהלים מקצועיים",
    url: "/professional-managers",
    icon: UserCog,
    tour: "tour-nav-managers",
  },
]

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const location = useLocation()
  const { recruiter } = useAuth()

  const isChildActive = (items: { url: string }[]) =>
    items.some((item) => location.pathname === item.url)

  const visibleNavItems = navItems.filter(
    (item) =>
      !('url' in item) ||
      item.url !== '/professional-managers' ||
      recruiter?.role === 'superuser'
  )

  return (
    <Sidebar collapsible="icon" side="right" data-tour="tour-sidebar" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              className="data-[slot=sidebar-menu-button]:!p-1.5"
            >
              <NavLink to="/dashboard">
                <span className="text-base font-semibold">מערכת גיוס</span>
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarMenu>
          {visibleNavItems.map((item) =>
            "items" in item ? (
              <SidebarMenuItem key={item.title} data-tour={item.tour}>
                <Collapsible
                  className="group"
                  defaultOpen
                >
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton tooltip={item.title} isActive={isChildActive(item.items!)}>
                      <item.icon />
                      <span>{item.title}</span>
                      <ChevronDown className="ml-auto transition-transform duration-200 group-data-[state=open]:rotate-180" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {item.items!.map((subItem) => (
                        <SidebarMenuSubItem key={subItem.url}>
                          <SidebarMenuSubButton
                            asChild
                            isActive={location.pathname === subItem.url}
                          >
                            <NavLink to={subItem.url}>
                              <subItem.icon />
                              <span>{subItem.title}</span>
                            </NavLink>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </Collapsible>
              </SidebarMenuItem>
            ) : (
              <SidebarMenuItem key={item.title} data-tour={item.tour}>
                <SidebarMenuButton asChild tooltip={item.title} isActive={location.pathname === item.url}>
                  <NavLink to={item.url}>
                    <item.icon />
                    <span>{item.title}</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )
          )}
          {recruiter?.role === 'recruiter' && (
            <SidebarMenuItem data-tour="tour-nav-settings">
              <SidebarMenuButton asChild tooltip="הגדרות" isActive={location.pathname === '/settings'}>
                <NavLink to="/settings">
                  <Settings />
                  <span>הגדרות</span>
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          {recruiter?.role === 'superuser' && (
            <SidebarMenuItem data-tour="tour-nav-users">
              <Collapsible className="group" defaultOpen>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton tooltip="משתמשים" isActive={location.pathname === '/users'}>
                    <UserPlus />
                    <span>משתמשים</span>
                    <ChevronDown className="ml-auto transition-transform duration-200 group-data-[state=open]:rotate-180" />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton asChild isActive={location.pathname === '/users'}>
                        <NavLink to="/users">
                          <UserPlus />
                          <span>ניהול משתמשים</span>
                        </NavLink>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  </SidebarMenuSub>
                </CollapsibleContent>
              </Collapsible>
            </SidebarMenuItem>
          )}
        </SidebarMenu>
      </SidebarContent>
      {/* <SidebarFooter>
        <NavUser />
      </SidebarFooter> */}
    </Sidebar>
  )
}
